<script setup>
import { ref, computed, nextTick } from 'vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import KnowledgeBaseAPI from 'dashboard/api/knowledgeBase';
import { useAlert } from 'dashboard/composables';

const emit = defineEmits(['saved']);

const dialogRef = ref(null);
const isEditing = ref(false);
const editingId = ref(null);
const isLoading = ref(false);

const form = ref({
  kind: 'faq',
  title: '',
  content: '',
  answer: '',
  enabled: true,
});

const isFormInvalid = computed(() => {
  return !form.value.title.trim() || !form.value.content.trim();
});

const open = (item = null) => {
  if (item) {
    isEditing.value = true;
    editingId.value = item.id;
    form.value = {
      kind: item.kind || 'faq',
      title: item.title || '',
      content: item.content || '',
      answer: item.answer || '',
      enabled: item.enabled !== false,
    };
  } else {
    isEditing.value = false;
    editingId.value = null;
    form.value = {
      kind: 'faq',
      title: '',
      content: '',
      answer: '',
      enabled: true,
    };
  }
  nextTick(() => {
    dialogRef.value?.open();
  });
};

const close = () => {
  dialogRef.value?.close();
};

const save = async () => {
  if (isFormInvalid.value) return;
  isLoading.value = true;
  try {
    const payload = {
      kind: form.value.kind,
      title: form.value.title.trim(),
      content: form.value.content.trim(),
      answer: form.value.kind === 'faq' ? form.value.answer.trim() : '',
      enabled: form.value.enabled,
    };

    if (isEditing.value) {
      await KnowledgeBaseAPI.updateItem(editingId.value, payload);
      useAlert('知识条目已更新 ✓');
    } else {
      await KnowledgeBaseAPI.createItem(payload);
      useAlert('知识条目已新增 ✓');
    }
    emit('saved');
    close();
  } catch (error) {
    useAlert(error.response?.data?.error || error.message || '操作失败');
  } finally {
    isLoading.value = false;
  }
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="isEditing ? '编辑知识条目' : '新建知识条目'"
    :confirm-button-label="isEditing ? '保存修改' : '确认添加'"
    cancel-button-label="取消"
    :is-loading="isLoading"
    :disable-confirm-button="isFormInvalid"
    width="2xl"
    @confirm="save"
  >
    <div class="flex flex-col gap-4 py-2">
      <!-- 类别选择 -->
      <div class="flex flex-col gap-1.5">
        <label class="text-xs font-medium text-n-slate-11">条目类型</label>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          <label
            class="flex items-start gap-2.5 p-2.5 rounded-lg border cursor-pointer transition-colors"
            :class="form.kind === 'faq' ? 'border-n-brand bg-n-brand/10 text-n-slate-12' : 'border-n-weak bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2'"
          >
            <input
              v-model="form.kind"
              type="radio"
              value="faq"
              class="mt-0.5 accent-n-brand"
            />
            <div class="flex flex-col">
              <span class="text-xs font-semibold text-n-slate-12">FAQ（常见问答对）</span>
              <span class="text-[11px] text-n-slate-10 leading-snug mt-0.5">精准匹配客户高频问题，AI 优先按预设标准答案直接回复</span>
            </div>
          </label>
          <label
            class="flex items-start gap-2.5 p-2.5 rounded-lg border cursor-pointer transition-colors"
            :class="form.kind === 'document' ? 'border-n-brand bg-n-brand/10 text-n-slate-12' : 'border-n-weak bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2'"
          >
            <input
              v-model="form.kind"
              type="radio"
              value="document"
              class="mt-0.5 accent-n-brand"
            />
            <div class="flex flex-col">
              <span class="text-xs font-semibold text-n-slate-12">文档片段 / 业务规则</span>
              <span class="text-[11px] text-n-slate-10 leading-snug mt-0.5">业务政策、规章说明或手册，由 AI 检索阅读后提炼总结回复</span>
            </div>
          </label>
        </div>
      </div>

      <!-- 标题 -->
      <div class="flex flex-col gap-1.5">
        <label class="text-xs font-medium text-n-slate-11">
          标题 / 客户常见问法 <span class="text-n-ruby-11">*</span>
        </label>
        <input
          v-model="form.title"
          type="text"
          placeholder="例如：退货政策、运费规则、如何开发票？"
          class="reset-base !mb-0 w-full px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12 placeholder:text-n-slate-10 focus:bg-n-alpha-2 focus:border-n-brand focus:outline-none focus:ring-1 focus:ring-n-brand"
        />
      </div>

      <!-- 正文 -->
      <div class="flex flex-col gap-1.5">
        <label class="text-xs font-medium text-n-slate-11">
          正文 / 政策内容（AI 检索与阅读依据） <span class="text-n-ruby-11">*</span>
        </label>
        <textarea
          v-model="form.content"
          rows="4"
          placeholder="输入详细的业务政策、规则说明。AI 将严格基于此正文回答客户咨询。"
          class="reset-base !mb-0 w-full px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12 placeholder:text-n-slate-10 focus:bg-n-alpha-2 focus:border-n-brand focus:outline-none focus:ring-1 focus:ring-n-brand"
        />
      </div>

      <!-- 参考答案 (FAQ 专用) -->
      <div v-if="form.kind === 'faq'" class="flex flex-col gap-1.5">
        <label class="text-xs font-medium text-n-slate-11">
          参考答案（可选）
        </label>
        <input
          v-model="form.answer"
          type="text"
          placeholder="预先写好的标准回复口径；留空则由 AI 根据正文自动提炼组织"
          class="reset-base !mb-0 w-full px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12 placeholder:text-n-slate-10 focus:bg-n-alpha-2 focus:border-n-brand focus:outline-none focus:ring-1 focus:ring-n-brand"
        />
      </div>

      <!-- 启用状态 -->
      <div class="flex items-center gap-2 pt-1">
        <input
          id="kb-enabled-checkbox"
          v-model="form.enabled"
          type="checkbox"
          class="rounded accent-n-brand size-4 cursor-pointer"
        />
        <label for="kb-enabled-checkbox" class="text-sm cursor-pointer select-none text-n-slate-12">
          启用此条目（停用后 AI 检索时将自动排除此条知识）
        </label>
      </div>
    </div>
  </Dialog>
</template>
